import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { useSeller } from "../contexts/SellerContext";
import "./FinalCorrection.css";
import PageHeader from "../components/PageHeader";
import { useNavigate } from "react-router-dom";

const PAYMENT_PREFERENCES = ["PRE_PICKUP", "WAREHOUSE", "BUYER_PAYMENT"];

const PAYMENT_LABELS = {
  PRE_PICKUP: "Pre-Pickup",
  WAREHOUSE: "At Operator",
  BUYER_PAYMENT: "After Buyer Pay",
};

const DEFAULT_PAYMENT_PREFERENCE = "BUYER_PAYMENT";

const ChevronDownIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="m5 12 4 4L19 6" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4 1 1-4Z" />
  </svg>
);

const PaymentDropdown = ({ value, locked, lockedLabel, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selected = options.find((opt) => opt.value === value) || options[0];

  const handleSelect = useCallback(
    (option) => {
      onChange(option.value);
      setOpen(false);
    },
    [onChange],
  );

  const handleToggle = useCallback(() => {
    if (locked) return;
    setOpen((prev) => !prev);
  }, [locked]);

  const handleOutsideClick = useCallback((event) => {
    if (wrapRef.current && !wrapRef.current.contains(event.target)) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open, handleOutsideClick]);

  return (
    <div
      className="fc-payment-chip-wrap"
      ref={wrapRef}
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className={`fc-payment-chip ${locked ? "is-locked" : ""} ${
          open ? "fc-payment-chip-open" : ""
        }`}
        onClick={handleToggle}
        disabled={locked}
        title={locked ? lockedLabel : "Payment preference"}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="fc-payment-chip-label">{selected.label}</span>
        <svg
          className="fc-payment-chip-arrow"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <ul
          className="fc-payment-menu"
          role="listbox"
          onClick={(event) => event.stopPropagation()}
        >
          {options.map((option) => {
            const isCurrent = option.value === selected.value;

            return (
              <li key={option.value}>
                <button
                  type="button"
                  className={`fc-payment-option ${
                    isCurrent ? "is-active" : ""
                  }`}
                  onClick={() => handleSelect(option)}
                >
                  <span>{option.label}</span>
                  {isCurrent && (
                    <svg
                      className="fc-payment-check"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path d="m5 12 4 4L19 6" />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const FinalCorrection = () => {
  const { seller } = useSeller();
  const sellerId = seller?.sellerId;
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [savingGroupId, setSavingGroupId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [tempEdits, setTempEdits] = useState({});
  const [groupPaymentPreferences, setGroupPaymentPreferences] = useState({});
  const [operatorNames, setOperatorNames] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});

  const [selectedVariant, setSelectedVariant] = useState(null);
  const [grossWt, setGrossWt] = useState("0");
  const [netWt, setNetWt] = useState("0");
  const [setCount, setSetCount] = useState("0");

  const getRequiredSizes = (order) => {
    const qtyBySize = {};

    (order.selectedVariants || []).forEach((variant) => {
      qtyBySize[variant.size] = parseInt(variant.quantity) || 0;
    });

    if (order.lotDetails?.length > 0) {
      return order.lotDetails
        .map((lot) => lot.size)
        .filter((size) => (qtyBySize[size] || 0) > 0);
    }

    return (order.sizes || []).filter((size) => (qtyBySize[size] || 0) > 0);
  };

  const areAllLotsEdited = (orderId, order) => {
    const requiredSizes = getRequiredSizes(order);
    const edits = tempEdits[orderId] || {};

    return requiredSizes.every((size) =>
      Object.prototype.hasOwnProperty.call(edits, size),
    );
  };

  const groupedOrders = useMemo(() => {
    const groups = {};

    orders.forEach((order) => {
      const operatorId = order.operatorId || "UNASSIGNED";

      if (!groups[operatorId]) {
        groups[operatorId] = [];
      }

      groups[operatorId].push(order);
    });

    return groups;
  }, [orders]);

  const groupKeys = useMemo(() => {
    return Object.keys(groupedOrders).sort((a, b) => {
      if (a === "UNASSIGNED") return 1;
      if (b === "UNASSIGNED") return -1;

      return a.localeCompare(b);
    });
  }, [groupedOrders]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!sellerId) return;

      try {
        setLoading(true);
        setError(null);

        const q = query(
          collection(db, "orderList"),
          where("sellerId", "==", sellerId),
          where("orderStatus", "==", "Assorted"),
        );

        const snapshot = await getDocs(q);

        const fetchedOrders = [];
        const groupPrefsFromOrders = {};

        snapshot.forEach((docSnap) => {
          const data = docSnap.data();

          fetchedOrders.push({
            firestoreId: docSnap.id,
            ...data,
            lotDetails: data.lotDetails || [],
            orderDetail: data.orderDetail || {},
          });

          const operatorId = data.operatorId || "UNASSIGNED";

          if (
            data.paymentPreference &&
            PAYMENT_PREFERENCES.includes(data.paymentPreference)
          ) {
            if (!groupPrefsFromOrders[operatorId]) {
              groupPrefsFromOrders[operatorId] = data.paymentPreference;
            } else if (
              groupPrefsFromOrders[operatorId] !== data.paymentPreference
            ) {
              groupPrefsFromOrders[operatorId] = "";
            }
          }
        });

        const initialTemp = {};

        fetchedOrders.forEach((order) => {
          const detail = order.orderDetail || {};
          const reconstructed = {};

          Object.entries(detail).forEach(([size, data]) => {
            const lot = order.lotDetails?.find((item) => item.size === size);

            const lotSet = lot ? parseInt(lot.set) || 0 : 0;

            reconstructed[size] = {
              grossWt:
                lotSet > 0 ? (parseFloat(data.grossWT) || 0) * lotSet : 0,
              netWt: lotSet > 0 ? (parseFloat(data.netwt) || 0) * lotSet : 0,
              set: lotSet,
              stampCertificateQtyPerSet:
                parseInt(data.stampCertificateQtyPerSet) || 0,
            };
          });

          initialTemp[order.firestoreId] = reconstructed;
        });

        const operatorIds = Array.from(
          new Set(
            fetchedOrders
              .map((order) => order.operatorId)
              .filter((id) => id && id !== "UNASSIGNED"),
          ),
        );

        const fetchedOperatorNames = {};

        await Promise.all(
          operatorIds.map(async (operatorId) => {
            try {
              const operatorSnap = await getDoc(
                doc(db, "operatorregistrations", operatorId),
              );

              if (operatorSnap.exists()) {
                const data = operatorSnap.data();

                fetchedOperatorNames[operatorId] =
                  data.organizationName ||
                  data.businessName ||
                  data.operatorName ||
                  operatorId;
              }
            } catch (operatorErr) {
              console.warn(
                `Failed to load operator ${operatorId}`,
                operatorErr,
              );
            }
          }),
        );

        const initialExpanded = {};

        fetchedOrders.forEach((order) => {
          const operatorId = order.operatorId || "UNASSIGNED";

          initialExpanded[operatorId] = true;

          if (!groupPrefsFromOrders[operatorId]) {
            groupPrefsFromOrders[operatorId] = DEFAULT_PAYMENT_PREFERENCE;
          }
        });

        setOrders(fetchedOrders);
        setGroupPaymentPreferences(groupPrefsFromOrders);
        setOperatorNames(fetchedOperatorNames);
        setTempEdits(initialTemp);
        setExpandedGroups(initialExpanded);
      } catch (err) {
        console.error("Error fetching orders:", err);
        setError("Failed to load orders.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [sellerId]);

  const handleProductClick = (product) => {
    navigate(`/product/${product.id}`, {
      state: { product },
    });
  };

  const openDrawer = (order, size, lot = null) => {
    const currentEdit = tempEdits[order.firestoreId]?.[size] || {};

    const variant = order.selectedVariants?.find((item) => item.size === size);

    const orderedQty = variant ? parseInt(variant.quantity) || 0 : 0;

    const lotAvgGrossWt = lot ? parseFloat(lot.avgGrossWt) || 0 : 0;

    const lotAvgNetWt = lot ? parseFloat(lot.avgNetWt) || 0 : 0;

    const defaultSet =
      currentEdit.set !== undefined ? currentEdit.set : orderedQty;

    const defaultGross =
      currentEdit.grossWt !== undefined
        ? currentEdit.grossWt
        : orderedQty * lotAvgGrossWt;

    const defaultNet =
      currentEdit.netWt !== undefined
        ? currentEdit.netWt
        : orderedQty * lotAvgNetWt;

    setSelectedVariant({
      order,
      size,
      lot,
    });

    setGrossWt(String(defaultGross));
    setNetWt(String(defaultNet));
    setSetCount(String(defaultSet));

    setDrawerOpen(true);
  };

  const applyDrawerEdits = () => {
    if (!selectedVariant) return;

    const { order, size } = selectedVariant;

    let setVal = Math.max(0, parseInt(setCount) || 0);
    let gross = Math.max(0, parseFloat(grossWt) || 0);
    let net = Math.max(0, parseFloat(netWt) || 0);

    if (setVal === 0) {
      gross = 0;
      net = 0;
    } else {
      net = Math.min(net, gross);
    }

    const variant = order.selectedVariants?.find((item) => item.size === size);

    const stampCertificateQtyPerSet = variant
      ? parseInt(variant.stampCertificateQtyPerSet) || 0
      : 0;

    setTempEdits((prev) => ({
      ...prev,
      [order.firestoreId]: {
        ...prev[order.firestoreId],
        [size]: {
          grossWt: gross,
          netWt: net,
          set: setVal,
          stampCertificateQtyPerSet,
        },
      },
    }));

    setDrawerOpen(false);
  };

  const handleGroupPaymentPreferenceChange = (groupId, preference) => {
    setGroupPaymentPreferences((prev) => ({
      ...prev,
      [groupId]: preference,
    }));
  };

  const buildFinalOrderDetail = (order) => {
    const edits = tempEdits[order.firestoreId] || {};

    const spec = order.specification;
    const orderDetail = {};

    Object.entries(edits).forEach(([size, data]) => {
      const setVal = data.set || 0;
      const gross = data.grossWt || 0;
      const net = data.netWt || 0;

      const avgGrossWt = setVal > 0 ? gross / setVal : 0;

      const avgNetWt = setVal > 0 ? net / setVal : 0;

      const avgSpecWt = avgGrossWt - avgNetWt;

      const variant = order.selectedVariants?.find(
        (item) => item.size === size,
      );

      const orderedQty = variant ? parseInt(variant.quantity) || 0 : 0;

      orderDetail[size] = {
        qty: orderedQty,
        grossWT: avgGrossWt,
        netwt: avgNetWt,
        ...(spec !== "PLANE" && {
          specWT: avgSpecWt,
        }),
        stampCertificateQtyPerSet: data.stampCertificateQtyPerSet || 0,
      };
    });

    return orderDetail;
  };

  const canMarkGroupRTD = (groupId, groupOrders) => {
    const allCorrected = groupOrders.every((order) =>
      areAllLotsEdited(order.firestoreId, order),
    );

    const paymentPreference =
      groupPaymentPreferences[groupId] || DEFAULT_PAYMENT_PREFERENCE;

    return allCorrected && PAYMENT_PREFERENCES.includes(paymentPreference);
  };

  const getGroupStats = (groupOrders) => {
    const total = groupOrders.length;

    const correctedCount = groupOrders.filter((order) =>
      areAllLotsEdited(order.firestoreId, order),
    ).length;

    return {
      total,
      correctedCount,
    };
  };

  const getOperatorDisplayName = (groupId, groupOrders) => {
    if (operatorNames[groupId]) {
      return operatorNames[groupId];
    }

    const orderWithName = groupOrders.find(
      (order) =>
        order.operatorName ||
        order.operatorOrganizationName ||
        order.operatorBusinessName ||
        order.operatorStoreName,
    );

    return (
      orderWithName?.operatorName ||
      orderWithName?.operatorOrganizationName ||
      orderWithName?.operatorBusinessName ||
      orderWithName?.operatorStoreName ||
      groupId
    );
  };

  const calculateOrderTotals = (order) => {
    const edits = tempEdits[order.firestoreId] || {};

    const netGramMc = parseFloat(order.netGramMc) || 0;

    const setMc = parseFloat(order.setMc) || 0;

    const stampPrice = parseFloat(order.stampCertificateCost) || 0;

    let totalGross = 0;
    let totalNet = 0;
    let totalSets = 0;
    let totalStampCost = 0;

    Object.values(edits).forEach((data) => {
      const setVal = data.set || 0;
      const gross = data.grossWt || 0;
      const net = data.netWt || 0;

      const stampQty = data.stampCertificateQtyPerSet || 0;

      totalGross += gross;
      totalNet += net;
      totalSets += setVal;

      totalStampCost += stampQty * setVal * stampPrice;
    });

    const totalMc = totalNet * netGramMc + totalSets * setMc;

    return {
      totalGross,
      totalNet,
      totalMc,
      totalStampCost,
      totalSets,
    };
  };

  const calculateGroupTotals = (groupOrders) => {
    return groupOrders.reduce(
      (totals, order) => {
        const value = calculateOrderTotals(order);

        totals.totalGross += value.totalGross;

        totals.totalNet += value.totalNet;

        totals.totalMc += value.totalMc;

        totals.totalStampCost += value.totalStampCost;

        totals.totalSets += value.totalSets;

        return totals;
      },
      {
        totalGross: 0,
        totalNet: 0,
        totalMc: 0,
        totalStampCost: 0,
        totalSets: 0,
      },
    );
  };

  const handleMarkAllAsRTD = async (groupId, groupOrders) => {
    const allCorrected = groupOrders.every((order) =>
      areAllLotsEdited(order.firestoreId, order),
    );

    const paymentPreference =
      groupPaymentPreferences[groupId] || DEFAULT_PAYMENT_PREFERENCE;

    if (!allCorrected) {
      alert(
        "Please complete final correction for all products before marking as RTD.",
      );
      return;
    }

    if (!PAYMENT_PREFERENCES.includes(paymentPreference)) {
      alert("Please select a payment preference before marking as RTD.");
      return;
    }

    setSavingGroupId(groupId);

    try {
      const batch = writeBatch(db);

      groupOrders.forEach((order) => {
        const orderDetail = buildFinalOrderDetail(order);

        batch.update(doc(db, "orderList", order.firestoreId), {
          orderDetail,
          orderStatus: "RTD",
          paymentPreference,
          buyerConfirmation: false,
          updatedAt: serverTimestamp(),
        });
      });

      await batch.commit();

      setOrders((prev) =>
        prev.filter((order) => {
          const operatorId = order.operatorId || "UNASSIGNED";

          return operatorId !== groupId;
        }),
      );

      setTempEdits((prev) => {
        const next = { ...prev };

        groupOrders.forEach((order) => {
          delete next[order.firestoreId];
        });

        return next;
      });

      setGroupPaymentPreferences((prev) => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });

      setExpandedGroups((prev) => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });

      alert(`Successfully marked ${groupOrders.length} order(s) as RTD.`);
    } catch (err) {
      console.error("RTD batch save error:", err);

      alert(`Failed to mark as RTD: ${err.message}`);
    } finally {
      setSavingGroupId(null);
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const fmt = (num) => Number(num || 0).toFixed(3);

  const fmtCurrency = (num) => Number(num || 0).toFixed(2);

  const renderOrderCard = (order) => {
    const imageUrl = order.images?.[0]?.url?.trim();
    const isEdited = areAllLotsEdited(order.firestoreId, order);
    const spec = order.specification;
    const orderedQtyBySize = {};
    (order.selectedVariants || []).forEach((variant) => {
      orderedQtyBySize[variant.size] = parseInt(variant.quantity) || 0;
    });
    let displayLots = [];
    if (order.lotDetails?.length > 0) {
      displayLots = order.lotDetails.map((lot) => {
        const size = lot.size;
        const edit = tempEdits[order.firestoreId]?.[size] || {};
        const orderedQty = orderedQtyBySize[size] || 0;
        let setVal =
          edit.set !== undefined ? edit.set : parseInt(lot.set) || orderedQty;
        let gross =
          edit.grossWt !== undefined
            ? edit.grossWt
            : (parseFloat(lot.avgGrossWt) || 0) * setVal;
        let net =
          edit.netWt !== undefined
            ? edit.netWt
            : (parseFloat(lot.avgNetWt) || 0) * setVal;
        if (setVal === 0) {
          gross = 0;
          net = 0;
        }
        const avgGrossWt = setVal > 0 ? gross / setVal : 0;
        const avgNetWt = setVal > 0 ? net / setVal : 0;
        return {
          size,
          set: setVal,
          grossWt: gross,
          netWt: net,
          avgGrossWt,
          avgNetWt,
          avgSpecWt: avgGrossWt - avgNetWt,
        };
      });
    } else {
      displayLots = (order.selectedVariants || []).map((variant) => {
        const size = variant.size;
        const edit = tempEdits[order.firestoreId]?.[size] || {};
        const orderedQty = parseInt(variant.quantity) || 0;
        const detail = order.orderDetail?.[size] || {};
        const setVal = edit.set !== undefined ? edit.set : orderedQty;
        const gross =
          edit.grossWt !== undefined
            ? edit.grossWt
            : (parseFloat(detail.grossWT) || 0) * setVal;
        const net =
          edit.netWt !== undefined
            ? edit.netWt
            : (parseFloat(detail.netwt) || 0) * setVal;
        const avgGrossWt = setVal > 0 ? gross / setVal : 0;
        const avgNetWt = setVal > 0 ? net / setVal : 0;
        return {
          size,
          set: setVal,
          grossWt: gross,
          netWt: net,
          avgGrossWt,
          avgNetWt,
          avgSpecWt: avgGrossWt - avgNetWt,
        };
      });
    }
    displayLots = displayLots.filter(
      (lot) => (orderedQtyBySize[lot.size] || 0) > 0,
    );
    const totalOrdered =
      order.selectedVariants?.reduce(
        (sum, variant) => sum + (parseInt(variant.quantity) || 0),
        0,
      ) || 0;
    const totalGross = displayLots.reduce(
      (sum, lot) => sum + (orderedQtyBySize[lot.size] || 0) * lot.avgGrossWt,
      0,
    );
    const totalNet = displayLots.reduce(
      (sum, lot) => sum + (orderedQtyBySize[lot.size] || 0) * lot.avgNetWt,
      0,
    );
    return (
      <article
        key={order.firestoreId}
        className={`fc-order-card ${isEdited ? "fc-order-card-complete" : ""}`}
      >
        <div className="fc-order-top">
          <button
            type="button"
            className="fc-product-image"
            onClick={() => handleProductClick(order)}
          >
            {imageUrl ? (
              <img src={imageUrl} alt={order.productName} />
            ) : (
              <span>No image</span>
            )}
          </button>
          <div className="fc-product-info">
            <div className="fc-product-heading">
              <h4>
                {order.category} /{order.productSource}/ {order.productName}
              </h4>

              {isEdited ? (
                <span className="fc-status fc-status-complete">
                  <CheckIcon />
                  Complete
                </span>
              ) : (
                <span className="fc-status fc-status-pending">Pending</span>
              )}
            </div>

            <p className="fc-product-subtitle">
              {order.specification} · {order.styleType}
            </p>

            <div className="fc-product-meta">
              <span>Wastage {order.wastage}%</span>

              {spec !== "PLANE" && (
                <span>
                  {spec} ₹{order.specificationGramRate}
                  /g
                </span>
              )}

              <span>MC ₹{order.netGramMc}/g</span>
            </div>
          </div>
        </div>

        <div className="fc-size-section">
          <div className="fc-section-label">
            <span>Size-wise correction</span>
            <span>Tap a row to edit</span>
          </div>

          <div
            className={`fc-size-table ${
              spec === "PLANE" ? "fc-size-table-plain" : ""
            }`}
          >
            <div className="fc-size-head">
              <span>Size</span>
              <span>Qty</span>
              <span>Gross</span>
              <span>Net</span>
              {spec !== "PLANE" && <span>{spec}</span>}
              <span></span>
            </div>

            {displayLots.map((lot) => {
              const orderedQty = orderedQtyBySize[lot.size] || 0;

              const rowEdited =
                tempEdits[order.firestoreId]?.[lot.size] !== undefined;

              return (
                <button
                  type="button"
                  key={lot.size}
                  className={`fc-size-row ${
                    rowEdited ? "fc-size-row-edited" : ""
                  }`}
                  onClick={() =>
                    openDrawer(
                      order,
                      lot.size,
                      order.lotDetails?.find(
                        (item) => item.size === lot.size,
                      ) || null,
                    )
                  }
                >
                  <span className="fc-size-value">{lot.size}</span>

                  <span className="fc-qty-value">{orderedQty}</span>

                  <span>{fmt(lot.avgGrossWt)}</span>

                  <span>{fmt(lot.avgNetWt)}</span>

                  {spec !== "PLANE" && <span>{fmt(lot.avgSpecWt)}</span>}

                  <span className="fc-row-action">
                    {rowEdited ? <CheckIcon /> : <EditIcon />}
                  </span>
                </button>
              );
            })}

            <div className="fc-size-total">
              <span>Total</span>
              <span>{totalOrdered}</span>
              <span>{fmt(totalGross)}</span>
              <span>{fmt(totalNet)}</span>

              {spec !== "PLANE" && <span>{fmt(totalGross - totalNet)}</span>}

              <span></span>
            </div>
          </div>
        </div>
      </article>
    );
  };

  if (loading) {
    return (
      <>
        <PageHeader title="Final Correction" />

        <div className="fc-page">
          <div className="fc-loading-state">
            <div className="fc-loading-spinner" />
            <strong>Loading correction queue</strong>
            <span>Preparing orders for final review...</span>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title="Final Correction" />

        <div className="fc-page">
          <div className="fc-error-state">
            <strong>{error}</strong>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Final Correction" />

      <main className="fc-page">
        <section className="fc-page-intro">
          <div>
            <h1>Correct & finalize orders</h1>
            <p>
              Review every size, confirm weights, choose the payment preference,
              then release the operator group to RTD.
            </p>
          </div>
        </section>

        {orders.length === 0 ? (
          <section className="fc-empty-state">
            <div className="fc-empty-icon">📦</div>

            <h2>Correction queue is clear</h2>

            <p>There are no assorted orders waiting for final correction.</p>
          </section>
        ) : (
          <section className="fc-groups">
            {groupKeys.map((groupId) => {
              const groupOrders = groupedOrders[groupId];

              const stats = getGroupStats(groupOrders);

              const groupTotals = calculateGroupTotals(groupOrders);

              const allCorrected = stats.correctedCount === stats.total;

              const operatorDisplayName = getOperatorDisplayName(
                groupId,
                groupOrders,
              );

              const groupPaymentPref =
                groupPaymentPreferences[groupId] || DEFAULT_PAYMENT_PREFERENCE;

              const canRTD = canMarkGroupRTD(groupId, groupOrders);

              const isExpanded = expandedGroups[groupId] !== false;

              const isSaving = savingGroupId === groupId;

              const progress =
                stats.total > 0
                  ? Math.round((stats.correctedCount / stats.total) * 100)
                  : 0;

              return (
                <section
                  key={groupId}
                  className={`fc-operator-card ${
                    canRTD ? "fc-operator-ready" : ""
                  }`}
                >
                  <div>
                    <div className="fc-operator-header">
                      <button
                        type="button"
                        className="fc-expand-button"
                        onClick={() => toggleGroup(groupId)}
                        aria-label={
                          isExpanded ? "Collapse operator" : "Expand operator"
                        }
                      >
                        {isExpanded ? (
                          <ChevronDownIcon />
                        ) : (
                          <ChevronRightIcon />
                        )}
                      </button>

                      <div className="fc-operator-main">
                        <div className="fc-operator-title-row">
                          <div className="fc-operator-title-block">
                            <h2>{operatorDisplayName}</h2>
                          </div>

                          <div
                            className={`fc-completion-pill ${
                              allCorrected ? "is-complete" : ""
                            }`}
                          >
                            {stats.correctedCount}/{stats.total} corrected
                          </div>
                        </div>

                        {/* Compact chip row: replaces the old boxy summary grid */}
                        <div className="fc-summary-chips">
                          <span className="fc-chip">
                            <span className="fc-chip-label">Orders</span>
                            <strong>{stats.total}</strong>
                          </span>

                          <span className="fc-chip">
                            <span className="fc-chip-label">Sets</span>
                            <strong>{groupTotals.totalSets}</strong>
                          </span>

                          <span className="fc-chip">
                            <span className="fc-chip-label">Gross</span>
                            <strong>{fmt(groupTotals.totalGross)}g</strong>
                          </span>

                          <span className="fc-chip">
                            <span className="fc-chip-label">Net</span>
                            <strong>{fmt(groupTotals.totalNet)}g</strong>
                          </span>

                          <span className="fc-chip fc-chip-accent">
                            <span className="fc-chip-label">Charges</span>
                            <strong>
                              ₹
                              {fmtCurrency(
                                groupTotals.totalMc +
                                  groupTotals.totalStampCost,
                              )}
                            </strong>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="fc-progress">
                      <div className="fc-progress-track">
                        <div
                          className="fc-progress-fill"
                          style={{
                            width: `${progress}%`,
                          }}
                        />
                      </div>

                      <span>{progress}%</span>
                      <PaymentDropdown
                        value={groupPaymentPref}
                        locked={!allCorrected}
                        lockedLabel="Complete all products first"
                        options={PAYMENT_PREFERENCES.map((pref) => ({
                          value: pref,
                          label: PAYMENT_LABELS[pref],
                        }))}
                        onChange={(value) =>
                          handleGroupPaymentPreferenceChange(groupId, value)
                        }
                      />
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="fc-operator-content">
                      <div className="fc-products-heading">
                        <span>Products</span>

                        <span>
                          {stats.correctedCount === stats.total
                            ? "All complete"
                            : `${stats.total - stats.correctedCount} remaining`}
                        </span>
                      </div>

                      <div className="fc-product-list">
                        {groupOrders.map(renderOrderCard)}
                      </div>

                      <div
                        className={`fc-rtd-panel ${canRTD ? "is-ready" : ""}`}
                      >
                        <div>
                          <h3>
                            {canRTD
                              ? "Ready for RTD"
                              : "Complete correction before RTD"}
                          </h3>

                          <p>
                            {canRTD
                              ? "All products and payment details are ready."
                              : "Every required size must be corrected before the group can be released."}
                          </p>
                        </div>

                        <button
                          type="button"
                          className="fc-rtd-button"
                          onClick={() =>
                            handleMarkAllAsRTD(groupId, groupOrders)
                          }
                          disabled={!canRTD || isSaving}
                        >
                          {isSaving ? (
                            <>
                              <span className="fc-button-spinner" />
                              Releasing...
                            </>
                          ) : (
                            <>
                              Mark All as RTD
                              <span>→</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              );
            })}
          </section>
        )}

        {drawerOpen && (
          <div
            className="fc-drawer-overlay"
            onClick={() => setDrawerOpen(false)}
          >
            <div
              className="fc-drawer"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="fc-drawer-handle" />

              <div className="fc-drawer-header">
                <div>
                  <span className="fc-drawer-kicker">Size correction</span>

                  <h2>Size {selectedVariant?.size}</h2>

                  <p>{selectedVariant?.order?.productName}</p>
                </div>

                <button
                  type="button"
                  className="fc-drawer-close"
                  onClick={() => setDrawerOpen(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div className="fc-drawer-body">
                <div className="fc-edit-context">
                  <span>Ordered quantity</span>
                  <strong>
                    {selectedVariant?.order?.selectedVariants?.find(
                      (variant) => variant.size === selectedVariant?.size,
                    )?.quantity || 0}{" "}
                    units
                  </strong>
                </div>

                <div className="fc-input-grid">
                  <div className="fc-field fc-field-wide">
                    <label>Total sets</label>

                    <input
                      type="number"
                      min="0"
                      inputMode="numeric"
                      value={setCount}
                      onChange={(event) => {
                        const value = event.target.value;

                        setSetCount(value);

                        if (value === "0" || value === "") {
                          setGrossWt("0");
                          setNetWt("0");
                        }
                      }}
                    />
                  </div>

                  <div className="fc-field">
                    <label>Gross weight</label>

                    <div className="fc-input-unit">
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        inputMode="decimal"
                        value={grossWt}
                        disabled={setCount === "0"}
                        onChange={(event) => {
                          const value = event.target.value;

                          setGrossWt(value);

                          const gross = parseFloat(value) || 0;

                          const net = parseFloat(netWt) || 0;

                          if (net > gross) {
                            setNetWt(String(gross));
                          }
                        }}
                      />

                      <span>g</span>
                    </div>
                  </div>

                  <div className="fc-field">
                    <label>Net weight</label>

                    <div className="fc-input-unit">
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        inputMode="decimal"
                        value={netWt}
                        disabled={setCount === "0"}
                        onChange={(event) => {
                          const value = event.target.value;

                          const net = parseFloat(value) || 0;

                          const gross = parseFloat(grossWt) || 0;

                          setNetWt(String(Math.min(net, gross)));
                        }}
                      />

                      <span>g</span>
                    </div>
                  </div>
                </div>

                {selectedVariant?.order?.specification !== "PLANE" && (
                  <div className="fc-derived-card">
                    <div>
                      <span>
                        Average {selectedVariant?.order?.specification}
                      </span>

                      <small>Gross − Net per set</small>
                    </div>

                    <strong>
                      {setCount !== "0" && setCount !== ""
                        ? (
                            (parseFloat(grossWt) - parseFloat(netWt)) /
                            (parseInt(setCount) || 1)
                          ).toFixed(3)
                        : "0.000"}{" "}
                      g
                    </strong>
                  </div>
                )}
              </div>

              <div className="fc-drawer-actions">
                <button
                  type="button"
                  className="fc-secondary-button"
                  onClick={() => setDrawerOpen(false)}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="fc-primary-button"
                  onClick={applyDrawerEdits}
                >
                  Apply correction
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default FinalCorrection;
